import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity, Platform, TextStyle } from "react-native";
import { CheckCircle, Warning, Lightbulb, CaretDown, CaretUp, Copy, Check } from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";

export interface AssignmentAnalysis {
  title: string;
  effectiveness?: string;
  feedback?: string;
  understandingGauge?: string;
  suggestions?: string[];
  assignmentId?: string;
  courseCode?: string;
  alternativeContent?: string;
  improvementRationale?: string;
}

interface EffectivenessBadgeProps {
  rating: string;
}

const EffectivenessBadge = ({ rating }: EffectivenessBadgeProps) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.effectivenessBadge, { backgroundColor: style.bg }]}>
      <CustomText useTranslationText={false} text={rating} size="xs" weight="medium" style={{ color: style.text }} />
    </View>
  );
};

interface AssignmentAccordionProps {
  analysis: AssignmentAnalysis;
}

/**
 * Formats text by:
 * - Converting \n to actual newlines
 * - Removing markdown bold markers (**)
 * - Cleaning up excessive whitespace
 */
const formatAlternativeContent = (text: string): string => {
  if (!text) return "";

  return (
    text
      // Replace literal \n with actual newlines
      .replace(/\\n/g, "\n")
      // Remove markdown bold markers
      .replace(/\*\*/g, "")
      // Remove markdown italic markers
      .replace(/\*/g, "")
      // Clean up multiple consecutive newlines
      .replace(/\n{3,}/g, "\n\n")
      // Trim whitespace
      .trim()
  );
};

const AssignmentAccordion = ({ analysis }: AssignmentAccordionProps) => {
  const [expanded, setExpanded] = useState(false);
  const [copiedAlternative, setCopiedAlternative] = useState(false);

  const getIcon = () => {
    switch (analysis.effectiveness?.toLowerCase()) {
      case "excellent":
        return <CheckCircle size={20} color="#027A48" />;
      case "good":
        return <CheckCircle size={20} color="#1d4ed8" />;
      case "fair":
        return <Warning size={20} color="#B54708" />;
      case "poor":
        return <Warning size={20} color="#dc2626" />;
      default:
        return <CheckCircle size={20} color="#667085" />;
    }
  };

  const getIconContainerColor = () => {
    switch (analysis.effectiveness?.toLowerCase()) {
      case "excellent":
        return "#f0fdf4";
      case "good":
        return "#eff6ff";
      case "fair":
        return "#fefce8";
      case "poor":
        return "#f7f4f2";
      default:
        return "#F2F4F7";
    }
  };

  const getDisplayTitle = () => {
    if (analysis.courseCode) {
      return `${analysis.courseCode}: ${analysis.title}`;
    }
    return analysis.title;
  };

  return (
    <TouchableOpacity style={styles.assignmentAccordion} onPress={() => setExpanded(!expanded)} activeOpacity={0.7}>
      <View style={styles.assignmentAccordionHeader}>
        <View style={[styles.iconContainer, { backgroundColor: getIconContainerColor() }]}>{getIcon()}</View>
        <View style={styles.assignmentAccordionContent}>
          <View style={styles.assignmentTitleRow}>
            <CustomText
              useTranslationText={false}
              text={getDisplayTitle()}
              size="m"
              weight="medium"
              style={styles.assignmentTitle}
            />
            <EffectivenessBadge rating={analysis.effectiveness || ""} />
          </View>
          <CustomText
            useTranslationText={false}
            text={analysis.understandingGauge || ""}
            size="s"
            style={styles.assignmentDescription}
            numberOfLines={expanded ? undefined : 2}
          />
        </View>
        {expanded ? <CaretUp size={16} color="#98A2B3" /> : <CaretDown size={16} color="#98A2B3" />}
      </View>

      {expanded && analysis.feedback && (
        <View style={styles.assignmentSuggestions}>
          <CustomText
            useTranslationText={false}
            text="Feedback"
            size="xs"
            weight="bold"
            style={styles.suggestionsLabel}
          />
          <CustomText useTranslationText={false} text={analysis.feedback} size="xs" style={styles.suggestionText} />
        </View>
      )}
      {expanded && analysis.suggestions && analysis.suggestions.length > 0 && (
        <View style={analysis.feedback ? styles.assignmentSuggestionsNoBorder : styles.assignmentSuggestions}>
          <CustomText
            useTranslationText={false}
            text="Suggestions for improvement"
            size="xs"
            weight="bold"
            style={styles.suggestionsLabel}
          />
          {analysis.suggestions.map((s: string, idx: number) => (
            <View key={idx} style={styles.suggestionItem}>
              <Lightbulb size={14} color="#F79009" />
              <CustomText useTranslationText={false} text={s} size="xs" style={styles.suggestionText} />
            </View>
          ))}
        </View>
      )}
      {expanded && analysis.alternativeContent && (
        <View style={styles.assignmentSuggestionsNoBorder}>
          <View style={styles.alternativeContentHeader}>
            <CustomText
              useTranslationText={false}
              text="Alternative Assignment Content"
              size="xs"
              weight="bold"
              style={styles.suggestionsLabel}
            />
            <TouchableOpacity
              style={styles.copyButton}
              onPress={() => {
                navigator.clipboard.writeText(formatAlternativeContent(analysis.alternativeContent || ""));
                setCopiedAlternative(true);
                setTimeout(() => setCopiedAlternative(false), 2000);
              }}>
              {copiedAlternative ? (
                <Check size={16} color="#12B76A" weight="bold" />
              ) : (
                <Copy size={16} color="#667085" weight="regular" />
              )}
            </TouchableOpacity>
          </View>
          {analysis.improvementRationale && (
            <View style={styles.rationaleContainer}>
              <CustomText
                useTranslationText={false}
                text="Reasoning"
                size="xs"
                weight="bold"
                style={styles.rationaleLabel}
              />
              <CustomText
                useTranslationText={false}
                text={analysis.improvementRationale}
                size="xs"
                style={styles.rationaleText}
              />
            </View>
          )}
          <View style={styles.alternativeContentContainer}>
            <CustomText
              useTranslationText={false}
              text={formatAlternativeContent(analysis.alternativeContent || "")}
              size="xs"
              style={styles.alternativeContentText}
            />
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  effectivenessBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  assignmentAccordion: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#EAECF0",
    padding: 16,
    overflow: "hidden",
  },
  assignmentAccordionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  assignmentAccordionContent: {
    flex: 1,
    minWidth: 0,
  },
  assignmentTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  assignmentTitle: {
    color: "#101828",
  },
  assignmentDescription: {
    color: "#475467",
    lineHeight: 20,
  },
  assignmentSuggestions: {
    marginTop: 16,
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
  },
  assignmentSuggestionsNoBorder: {
    marginTop: 4,
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  suggestionsLabel: {
    color: "#667085",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  alternativeContentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  copyButton: {
    padding: 4,
    borderRadius: 4,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 6,
  },
  suggestionText: {
    color: "#475467",
    flex: 1,
    lineHeight: 18,
  },
  alternativeContentContainer: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  alternativeContentText: {
    color: "#344054",
    lineHeight: 20,
    fontSize: 13,
    ...(Platform.OS === "web"
      ? ({
          whiteSpace: "pre-line",
        } as unknown as TextStyle)
      : {}),
  },
  rationaleContainer: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E4E7EC",
  },
  rationaleLabel: {
    color: "#667085",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontSize: 10,
  },
  rationaleText: {
    color: "#475467",
    lineHeight: 18,
    fontSize: 12,
  },
});

export default AssignmentAccordion;
